/**
 * Design token names should follow the token naming convention:
 * See: https://github.com/ni/nimble/blob/main/packages/nimble-components/CONTRIBUTING.md#token-naming
 */

import type * as TokensNamespace from './design-tokens';

type TokenName = keyof typeof TokensNamespace;

export const tokenNames: { readonly [key in TokenName]: string } = {
    actionRgbPartialColor: 'action-rgb-partial-color',
    applicationBackgroundColor: 'application-background-color',
    dividerBackgroundColor: 'divider-background-color',
    headerBackgroundColor: 'header-background-color',
    sectionBackgroundColor: 'section-background-color',
    sectionBackgroundImage: 'section-background-image',
    buttonFillPrimaryColor: 'button-fill-primary-color',
    buttonPrimaryFontColor: 'button-primary-font-color',
    buttonFillAccentColor: 'button-fill-accent-color',
    buttonAccentBlockFontColor: 'button-accent-block-font-color',
    buttonAccentOutlineFontColor: 'button-accent-outline-font-color',
    buttonBorderAccentOutlineColor: 'button-border-accent-outline-color',
    fillSelectedColor: 'fill-selected-color',
    fillSelectedRgbPartialColor: 'fill-selected-rgb-partial-color',
    fillHoverSelectedColor: 'fill-hover-selected-color',
    fillHoverColor: 'fill-hover-color',
    fillHoverRgbPartialColor: 'fill-hover-rgb-partial-color',
    fillDownColor: 'fill-down-color',
    borderColor: 'border-color',
    borderRgbPartialColor: 'border-rgb-partial-color',
    failColor: 'fail-color',
    warningColor: 'warning-color',
    passColor: 'pass-color',
    informationColor: 'information-color',
    borderHoverColor: 'border-hover-color',
    iconColor: 'icon-color',
    modalBackdropColor: 'modal-backdrop-color',
    popupBorderColor: 'popup-border-color',
    cardBorderColor: 'card-border-color',
    tagFillColor: 'tag-fill-color',
    controlHeight: 'control-height',
    controlSlimHeight: 'control-slim-height',
    smallPadding: 'small-padding',
    mediumPadding: 'medium-padding',
    standardPadding: 'standard-padding',
    largePadding: 'large-padding',
    labelHeight: 'label-height',
    borderWidth: 'border-width',
    dividerWidth: 'divider-width',
    iconSize: 'icon-size',
    groupHeaderTextTransform: 'group-header-text-transform',
    drawerWidth: 'drawer-width',
    dialogSmallWidth: 'dialog-small-width',
    dialogSmallHeight: 'dialog-small-height',
    dialogSmallMaxHeight: 'dialog-small-max-height',
    dialogLargeWidth: 'dialog-large-width',
    dialogLargeHeight: 'dialog-large-height',
    dialogLargeMaxHeight: 'dialog-large-max-height',
    menuMinWidth: 'menu-min-width',
    bannerGapSize: 'banner-gap-size',
    spinnerSmallHeight: 'spinner-small-height',
    spinnerMediumHeight: 'spinner-medium-height',
    spinnerLargeHeight: 'spinner-large-height',
    tableFitRowsHeight: 'table-fit-rows-height',
    smallDelay: 'small-delay',
    mediumDelay: 'medium-delay',
    largeDelay: 'large-delay',
    headlinePlus1Font: 'headline-plus-1-font',
    headlinePlus1FontColor: 'headline-plus-1-font-color',
    headlinePlus1DisabledFontColor: 'headline-plus-1-disabled-font-color',
    headlinePlus1FontFamily: 'headline-plus-1-font-family',
    headlinePlus1FontSize: 'headline-plus-1-font-size',
    headlinePlus1FontWeight: 'headline-plus-1-font-weight',
    headlinePlus1FontLineHeight: 'headline-plus-1-font-line-height',
    headlineFont: 'headline-font',
    headlineFontColor: 'headline-font-color',
    headlineDisabledFontColor: 'headline-disabled-font-color',
    headlineFontFamily: 'headline-font-family',
    headlineFontSize: 'headline-font-size',
    headlineFontWeight: 'headline-font-weight',
    headlineFontLineHeight: 'headline-font-line-height',
    tableHeaderFont: 'table-header-font',
    tableHeaderFontColor: 'table-header-font-color',
    tableHeaderDisabledFontColor: 'table-header-disabled-font-color',
    tableHeaderFontFamily: 'table-header-font-family',
    tableHeaderFontSize: 'table-header-font-size',
    tableHeaderFontWeight: 'table-header-font-weight',
    tableHeaderFontLineHeight: 'table-header-font-line-height',
    titlePlus2Font: 'title-plus-2-font',
    titlePlus2FontColor: 'title-plus-2-font-color',
    titlePlus2DisabledFontColor: 'title-plus-2-disabled-font-color',
    titlePlus2FontFamily: 'title-plus-2-font-family',
    titlePlus2FontSize: 'title-plus-2-font-size',
    titlePlus2FontWeight: 'title-plus-2-font-weight',
    titlePlus2FontLineHeight: 'title-plus-2-font-line-height',
    titlePlus1Font: 'title-plus-1-font',
    titlePlus1FontColor: 'title-plus-1-font-color',
    titlePlus1DisabledFontColor: 'title-plus-1-disabled-font-color',
    titlePlus1FontFamily: 'title-plus-1-font-family',
    titlePlus1FontSize: 'title-plus-1-font-size',
    titlePlus1FontWeight: 'title-plus-1-font-weight',
    titlePlus1FontLineHeight: 'title-plus-1-font-line-height',
    titleFont: 'title-font',
    titleFontColor: 'title-font-color',
    titleDisabledFontColor: 'title-disabled-font-color',
    titleFontFamily: 'title-font-family',
    titleFontSize: 'title-font-size',
    titleFontWeight: 'title-font-weight',
    titleFontLineHeight: 'title-font-line-height',
    subtitlePlus1Font: 'subtitle-plus-1-font',
    subtitlePlus1FontColor: 'subtitle-plus-1-font-color',
    subtitlePlus1DisabledFontColor: 'subtitle-plus-1-disabled-font-color',
    subtitlePlus1FontFamily: 'subtitle-plus-1-font-family',
    subtitlePlus1FontSize: 'subtitle-plus-1-font-size',
    subtitlePlus1FontWeight: 'subtitle-plus-1-font-weight',
    subtitlePlus1FontLineHeight: 'subtitle-plus-1-font-line-height',
    subtitleFont: 'subtitle-font',
    subtitleFontColor: 'subtitle-font-color',
    subtitleDisabledFontColor: 'subtitle-disabled-font-color',
    subtitleFontFamily: 'subtitle-font-family',
    subtitleFontSize: 'subtitle-font-size',
    subtitleFontWeight: 'subtitle-font-weight',
    subtitleFontLineHeight: 'subtitle-font-line-height',
    linkFont: 'link-font',
    linkFontColor: 'link-font-color',
    linkDisabledFontColor: 'link-disabled-font-color',
    linkFontFamily: 'link-font-family',
    linkFontSize: 'link-font-size',
    linkFontWeight: 'link-font-weight',
    linkFontLineHeight: 'link-font-line-height',
    linkActiveFont: 'link-active-font',
    linkActiveFontColor: 'link-active-font-color',
    linkActiveDisabledFontColor: 'link-active-disabled-font-color',
    linkActiveFontFamily: 'link-active-font-family',
    linkActiveFontSize: 'link-active-font-size',
    linkActiveFontWeight: 'link-active-font-weight',
    linkActiveFontLineHeight: 'link-active-font-line-height',
    linkProminentFont: 'link-prominent-font',
    linkProminentFontColor: 'link-prominent-font-color',
    linkProminentDisabledFontColor: 'link-prominent-disabled-font-color',
    linkProminentFontFamily: 'link-prominent-font-family',
    linkProminentFontSize: 'link-prominent-font-size',
    linkProminentFontWeight: 'link-prominent-font-weight',
    linkProminentFontLineHeight: 'link-prominent-font-line-height',
    linkActiveProminentFont: 'link-active-prominent-font',
    linkActiveProminentFontColor: 'link-active-prominent-font-color',
    linkActiveProminentDisabledFontColor:
        'link-active-prominent-disabled-font-color',
    linkActiveProminentFontFamily: 'link-active-prominent-font-family',
    linkActiveProminentFontSize: 'link-active-prominent-font-size',
    linkActiveProminentFontWeight: 'link-active-prominent-font-weight',
    linkActiveProminentFontLineHeight: 'link-active-prominent-font-line-height',
    placeholderFont: 'placeholder-font',
    placeholderFontColor: 'placeholder-font-color',
    placeholderDisabledFontColor: 'placeholder-disabled-font-color',
    placeholderFontFamily: 'placeholder-font-family',
    placeholderFontSize: 'placeholder-font-size',
    placeholderFontWeight: 'placeholder-font-weight',
    placeholderFontLineHeight: 'placeholder-font-line-height',
    bodyFont: 'body-font',
    bodyFontColor: 'body-font-color',
    bodyDisabledFontColor: 'body-disabled-font-color',
    bodyFontFamily: 'body-font-family',
    bodyFontSize: 'body-font-size',
    bodyFontWeight: 'body-font-weight',
    bodyFontLineHeight: 'body-font-line-height',
    bodyEmphasizedFont: 'body-emphasized-font',
    bodyEmphasizedFontColor: 'body-emphasized-font-color',
    bodyEmphasizedDisabledFontColor: 'body-emphasized-disabled-font-color',
    bodyEmphasizedFontFamily: 'body-emphasized-font-family',
    bodyEmphasizedFontSize: 'body-emphasized-font-size',
    bodyEmphasizedFontWeight: 'body-emphasized-font-weight',
    bodyEmphasizedFontLineHeight: 'body-emphasized-font-line-height',
    bodyPlus1Font: 'body-plus-1-font',
    bodyPlus1FontColor: 'body-plus-1-font-color',
    bodyPlus1DisabledFontColor: 'body-plus-1-disabled-font-color',
    bodyPlus1FontFamily: 'body-plus-1-font-family',
    bodyPlus1FontSize: 'body-plus-1-font-size',
    bodyPlus1FontWeight: 'body-plus-1-font-weight',
    bodyPlus1FontLineHeight: 'body-plus-1-font-line-height',
    bodyPlus1EmphasizedFont: 'body-plus-1-emphasized-font',
    bodyPlus1EmphasizedFontColor: 'body-plus-1-emphasized-font-color',
    bodyPlus1EmphasizedDisabledFontColor:
        'body-plus-1-emphasized-disabled-font-color',
    bodyPlus1EmphasizedFontFamily: 'body-plus-1-emphasized-font-family',
    bodyPlus1EmphasizedFontSize: 'body-plus-1-emphasized-font-size',
    bodyPlus1EmphasizedFontWeight: 'body-plus-1-emphasized-font-weight',
    bodyPlus1EmphasizedFontLineHeight:
        'body-plus-1-emphasized-font-line-height',
    groupHeaderFont: 'group-header-font',
    groupHeaderFontColor: 'group-header-font-color',
    groupHeaderDisabledFontColor: 'group-header-disabled-font-color',
    groupHeaderFontFamily: 'group-header-font-family',
    groupHeaderFontSize: 'group-header-font-size',
    groupHeaderFontWeight: 'group-header-font-weight',
    groupHeaderFontLineHeight: 'group-header-font-line-height',
    controlLabelFont: 'control-label-font',
    controlLabelFontColor: 'control-label-font-color',
    controlLabelDisabledFontColor: 'control-label-disabled-font-color',
    controlLabelFontFamily: 'control-label-font-family',
    controlLabelFontSize: 'control-label-font-size',
    controlLabelFontWeight: 'control-label-font-weight',
    controlLabelFontLineHeight: 'control-label-font-line-height',
    buttonLabelFont: 'button-label-font',
    buttonLabelFontColor: 'button-label-font-color',
    buttonLabelDisabledFontColor: 'button-label-disabled-font-color',
    buttonLabelFontFamily: 'button-label-font-family',
    buttonLabelFontSize: 'button-label-font-size',
    buttonLabelFontWeight: 'button-label-font-weight',
    buttonLabelFontLineHeight: 'button-label-font-line-height',
    tooltipCaptionFont: 'tooltip-caption-font',
    tooltipCaptionFontColor: 'tooltip-caption-font-color',
    tooltipCaptionDisabledFontColor: 'tooltip-caption-disabled-font-color',
    tooltipCaptionFontFamily: 'tooltip-caption-font-family',
    tooltipCaptionFontSize: 'tooltip-caption-font-size',
    tooltipCaptionFontWeight: 'tooltip-caption-font-weight',
    tooltipCaptionFontLineHeight: 'tooltip-caption-font-line-height',
    tooltipBackgroundColor: 'tooltip-background-color',
    errorTextFont: 'error-text-font',
    errorTextFontColor: 'error-text-font-color',
    errorTextDisabledFontColor: 'error-text-disabled-font-color',
    errorTextFontFamily: 'error-text-font-family',
    errorTextFontSize: 'error-text-font-size',
    errorTextFontWeight: 'error-text-font-weight',
    errorTextFontLineHeight: 'error-text-font-line-height',
    tableRowBorderColor: 'table-row-border-color',
    elevation1BoxShadow: 'elevation-1-box-shadow',
    elevation2BoxShadow: 'elevation-2-box-shadow',
    elevation3BoxShadow: 'elevation-3-box-shadow',
    graphGridlineColor: 'graph-gridline-color',
    graphTrace1Color: 'graph-trace-1-color',
    graphTrace2Color: 'graph-trace-2-color',
    graphTrace3Color: 'graph-trace-3-color',
    graphTrace4Color: 'graph-trace-4-color',
    graphTrace5Color: 'graph-trace-5-color',
    graphTrace6Color: 'graph-trace-6-color',
    graphTrace7Color: 'graph-trace-7-color',
    graphTrace8Color: 'graph-trace-8-color',
    mentionFont: 'mention-font',
    mentionFontColor: 'mention-font-color',
    mentionDisabledFontColor: 'mention-disabled-font-color',
    mentionFontFamily: 'mention-font-family',
    mentionFontSize: 'mention-font-size',
    mentionFontWeight: 'mention-font-weight',
    mentionFontLineHeight: 'mention-font-line-height',
    calendarEventBackgroundStaticColor:
        'calendar-event-background-static-color',
    calendarEventBackgroundDynamicColor:
        'calendar-event-background-dynamic-color',
    calendarEventBackgroundTransientColor:
        'calendar-event-background-transient-color',
    calendarEventBorderStaticColor: 'calendar-event-border-static-color',
    calendarEventBorderTransientColor: 'calendar-event-border-transient-color',
    calendarEventStaticFontColor: 'calendar-event-static-font-color',
    calendarEventDynamicFontColor: 'calendar-event-dynamic-font-color',
    calendarEventTransientFontColor: 'calendar-event-transient-font-color',
    calendarEventBackgroundHoverStaticColor:
        'calendar-event-background-hover-static-color',
    calendarEventBackgroundHoverDynamicColor:
        'calendar-event-background-hover-dynamic-color',
    calendarEventBackgroundHoverTransientColor:
        'calendar-event-background-hover-transient-color',
    calendarEventOuterBorderHighlightedColor:
        'calendar-event-outer-border-highlighted-color',
    calendarRowBackgroundSelectedColor:
        'calendar-row-background-selected-color',
    calendarRowBackgroundConflictColor:
        'calendar-row-background-conflict-color',
    calendarEventFillBlockedColor: 'calendar-event-fill-blocked-color',
    calendarGrabHandleBackgroundColor: 'calendar-grab-handle-background-color',
    calendarGridBorderColor: 'calendar-grid-border-color',
    calendarGroupHeaderBackgroundColor: 'calendar-group-header-background-color'
};

const prefix = 'ni-nimble';

export const styleNameFromTokenName = (tokenName: string): string => `${prefix}-${tokenName}`;
export const cssPropertyFromTokenName = (tokenName: string): string => `--${prefix}-${tokenName}`;
export const scssPropertyFromTokenName = (tokenName: string): string => `$${prefix}-${tokenName}`;
export const scssPropertySetterMarkdown = (
    tokenName: string,
    cssProperty: string
): string => `\`${cssProperty}: $${prefix}-${tokenName};\``;
export const scssInternalPropertyFromTokenName = (tokenName: string): string => `$${prefix}-internal-${tokenName}`;
export const scssInternalPropertySetterMarkdown = (
    tokenName: string,
    property: string
): string => `\`#{$${prefix}-internal-${tokenName}}: ${property};\``;

// Order of suffixes in the array matter, as we want single word suffixes after the multi-word ones
const tokenSuffixes = [
    'RgbPartialColor',
    'DisabledFontColor',
    'FontColor',
    'FontLineHeight',
    'FontWeight',
    'FontSize',
    'TextTransform',
    'FontFamily',
    'BoxShadow',
    'MaxHeight',
    'MinWidth',
    'Font',
    'Size',
    'Width',
    'Height',
    'Delay',
    'Padding',
    'Color',
    'Image'
] as const;
export type TokenSuffix = (typeof tokenSuffixes)[number];
export const suffixFromTokenName = (
    tokenName: string
): TokenSuffix | undefined => tokenSuffixes[
    tokenSuffixes.findIndex(tokenSuffix => tokenName.endsWith(tokenSuffix))
];
